import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  Loader2,
  Plane,
  RefreshCw,
  SearchCheck,
  ShieldAlert,
  Wrench,
  XCircle,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { OrganizationPicker } from '../common';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import FlightDataPrecedencePanel from './FlightDataPrecedencePanel';

const ACTION_LABELS = {
  create_flight_document: 'Create flight document',
  update_flight_document: 'Update flight document',
  update_trip_component: 'Update trip component',
  refresh_current_flight_data: 'Refresh FlightAware data',
  resolve_flightaware_data: 'Resolve FlightAware schedule/live data',
  register_alerts: 'Register alerts',
  reregister_alerts: 'Re-register with current FlightAware account',
  verify_alert_registration: 'Verify provider callback',
  migrate_legacy_flight: 'Migrate legacy flight record',
  normalize_component_type: 'Normalize legacy flight type',
  skip_missing_flight_data: 'Skipped: missing flight data',
  override_package_restrictions: 'Override package restrictions',
  recently_checked_flightaware: 'Recently checked — retry deferred',
  aerodatabox_supplement: 'Fill empty fields from AeroDataBox',
};

const REQUIRED_RECONCILIATION_VERSION = 7;

const formatError = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail) return JSON.stringify(detail);
  return error?.message || 'The reconciliation request failed.';
};

const MetricCard = ({ label, value, tone = 'blue', detail }) => {
  const tones = {
    blue: 'border-ui-blue-line bg-ui-blue-bg text-ui-blue-fg',
    amber: 'border-ui-amber-line bg-ui-amber-bg text-ui-amber-fg',
    green: 'border-ui-green-line bg-ui-green-bg text-ui-green-fg',
    purple: 'border-ui-violet-line bg-ui-violet-bg text-ui-violet-fg',
  };

  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <div className="text-2xl font-semibold">{value ?? 0}</div>
      <div className="mt-1 text-sm font-medium">{label}</div>
      {detail && <div className="mt-1 text-xs opacity-75">{detail}</div>}
    </div>
  );
};

const EntitlementBadge = ({ enabled, label }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
    enabled
      ? 'bg-ui-green-bg text-ui-green-fg'
      : 'bg-ui-red-bg text-ui-red-fg'
  }`}>
    {enabled ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
    {label}: {enabled ? 'Enabled' : 'Disabled'}
  </span>
);

const FlightAwareReconciliation = () => {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [result, setResult] = useState(null);
  const [resultMode, setResultMode] = useState(null);
  const [loadingMode, setLoadingMode] = useState(null);
  const [error, setError] = useState('');
  const [overridePackageRestrictions, setOverridePackageRestrictions] = useState(true);

  const isSuperadmin = user?.role === 'superadmin';
  const selectedOrganization = useMemo(
    () => organizations.find((organization) => organization.id === selectedOrganizationId),
    [organizations, selectedOrganizationId],
  );
  const previewIsCurrent = resultMode === 'preview' && result?.organization_id === selectedOrganizationId;
  const backendIsCurrent = result?.reconciliation_version === REQUIRED_RECONCILIATION_VERSION;

  useEffect(() => {
    if (!isSuperadmin) return;
    let active = true;

    const loadOrganizations = async () => {
      setOrganizationsLoading(true);
      try {
        const response = await axios.get(`${getApiBase()}/admin/organizations`, {
          headers: getAuthHeaders(),
        });
        if (!active) return;
        const orgs = Array.isArray(response.data) ? response.data : [];
        setOrganizations(orgs);
      } catch (requestError) {
        if (active) setError(formatError(requestError));
      } finally {
        if (active) setOrganizationsLoading(false);
      }
    };

    loadOrganizations();
    return () => { active = false; };
  }, [isSuperadmin]);

  const changeOrganization = (organizationId) => {
    setSelectedOrganizationId(organizationId);
    setResult(null);
    setResultMode(null);
    setError('');
  };

  const runReconciliation = async (dryRun) => {
    if (!selectedOrganizationId) return;
    const mode = dryRun ? 'preview' : 'apply';

    if (!dryRun) {
      const confirmed = window.confirm(
        `Apply FlightAware reconciliation to ${selectedOrganization?.name || selectedOrganizationId}? ` +
        'This will update flight records, call FlightAware, and repair provider registrations using the current Render credentials.' +
        (overridePackageRestrictions ? ' Package restrictions will be overridden for this repair run.' : ''),
      );
      if (!confirmed) return;
    }

    setLoadingMode(mode);
    setError('');
    try {
      const response = await axios.post(
        `${getApiBase()}/admin/flightaware/reconcile`,
        null,
        {
          headers: getAuthHeaders(),
          params: {
            organization_id: selectedOrganizationId,
            dry_run: dryRun,
            override_package_restrictions: overridePackageRestrictions,
          },
        },
      );
      setResult(response.data);
      setResultMode(mode);
    } catch (requestError) {
      setError(formatError(requestError));
    } finally {
      setLoadingMode(null);
    }
  };

  if (!isSuperadmin) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center">
        <ShieldAlert className="mx-auto h-12 w-12 text-ui-red-solid" />
        <h1 className="mt-4 text-xl font-semibold text-ui-ink">Access denied</h1>
        <p className="mt-2 text-ui-ink-3">FlightAware reconciliation requires superadmin access.</p>
      </div>
    );
  }

  const cardClass = 'border-ui-line bg-ui-surface text-ui-ink';
  const mutedText = 'text-ui-ink-3';

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-3 py-4 sm:px-6 sm:py-6">
      <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-ui-teal-strong via-ui-blue-strong to-ui-indigo-strong p-6 text-ui-on-primary shadow-ui-pop">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-2xl bg-ui-on-primary/15 p-3"><Plane className="h-7 w-7" /></div>
            <div>
              <h1 className="text-2xl font-bold">FlightAware Reconciliation</h1>
              <p className="mt-1 max-w-3xl text-sm text-ui-on-primary/80">
                Audit an organization’s filed flights, refresh operational details, and verify or recreate provider registrations using the current FlightAware account.
              </p>
            </div>
          </div>
          <div className="rounded-xl bg-ui-on-primary/10 px-4 py-3 text-xs text-ui-on-primary/80">
            Preview is required before changes can be applied.
          </div>
        </div>
      </div>

      <section className={`rounded-2xl border p-5 shadow-ui-card ${cardClass}`}>
        <div className="flex items-center gap-2">
          <SearchCheck className="h-5 w-5 text-ui-link" />
          <h2 className="text-lg font-semibold">1. Select and inspect</h2>
        </div>
        <p className={`mt-1 text-sm ${mutedText}`}>The preview is read-only and includes the package entitlements that control verification and alerts.</p>

        <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
          <OrganizationPicker
            organizations={organizations}
            value={selectedOrganizationId}
            onChange={changeOrganization}
            loading={organizationsLoading}
            disabled={Boolean(loadingMode)}
            label="Organization"
            placeholder="Search for an organization"
            required
          />
          <button
            type="button"
            onClick={() => runReconciliation(true)}
            disabled={!selectedOrganizationId || Boolean(loadingMode)}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-ui-primary px-5 text-sm font-semibold text-ui-on-primary transition hover:bg-ui-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loadingMode === 'preview' ? <Loader2 className="h-4 w-4 animate-spin" /> : <SearchCheck className="h-4 w-4" />}
            Run dry-run preview
          </button>
        </div>
        <label className="mt-4 flex items-start gap-3 rounded-xl border border-ui-line bg-ui-hover/60 p-4 text-sm text-ui-ink">
          <input
            type="checkbox"
            checked={overridePackageRestrictions}
            onChange={(event) => {
              setOverridePackageRestrictions(event.target.checked);
              setResult(null);
              setResultMode(null);
            }}
            disabled={Boolean(loadingMode)}
            className="mt-0.5 h-4 w-4 rounded border-ui-control text-ui-primary"
          />
          <span>
            <span className="block font-semibold">Override package restrictions for this repair</span>
            <span className={`mt-0.5 block ${mutedText}`}>Allows the superadmin reconciliation to verify flights and restore alerts even when a legacy package is missing the newer FlightAware entitlement fields.</span>
          </span>
        </label>
      </section>

      {error && (
        <div role="alert" className="flex items-start gap-3 rounded-xl border border-ui-red-line bg-ui-red-bg p-4 text-ui-red-fg">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-none" />
          <div><div className="font-semibold">Reconciliation failed</div><div className="mt-1 text-sm">{error}</div></div>
        </div>
      )}

      {result && (
        <>
          <section className={`rounded-2xl border p-5 shadow-ui-card ${cardClass}`}>
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  {resultMode === 'apply' ? <CheckCircle2 className="h-5 w-5 text-ui-green-solid" /> : <Database className="h-5 w-5 text-ui-link" />}
                  <h2 className="text-lg font-semibold">{resultMode === 'apply' ? 'Reconciliation completed' : 'Dry-run results'}</h2>
                </div>
                <p className={`mt-1 text-sm ${mutedText}`}>
                  {result.organization_name || selectedOrganization?.name} · Package: {result.active_package_name || 'No active package'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <EntitlementBadge enabled={result.flight_verification_enabled} label="Verification" />
                <EntitlementBadge enabled={result.flight_alerts_enabled} label="Alerts" />
              </div>
            </div>

            {result.package_restrictions_overridden && (
              <div className="mt-4 flex gap-3 rounded-xl border border-ui-blue-line bg-ui-blue-bg p-4 text-ui-blue-fg">
                <ShieldAlert className="mt-0.5 h-5 w-5 flex-none" />
                <p className="text-sm">Package restrictions are being overridden for this superadmin repair run. Normal flight creation remains governed by the organization’s package.</p>
              </div>
            )}

            {!backendIsCurrent && (
              <div className="mt-4 flex gap-3 rounded-xl border border-ui-red-line bg-ui-red-bg p-4 text-ui-red-fg">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-none" />
                <p className="text-sm">The frontend is newer than the deployed backend reconciliation worker. Apply is disabled until backend version {REQUIRED_RECONCILIATION_VERSION} is live.</p>
              </div>
            )}

            {(!result.flight_verification_enabled || !result.flight_alerts_enabled) && (
              <div className="mt-4 flex gap-3 rounded-xl border border-ui-amber-line bg-ui-amber-bg p-4 text-ui-amber-fg">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-none" />
                <p className="text-sm">
                  The active package disables one or more FlightAware capabilities. Reconciliation will preserve and repair stored data, but restricted API refreshes or alert registrations will be skipped.
                </p>
              </div>
            )}

            {result.reconciliation_log_persisted === false && (
              <div className="mt-4 flex gap-3 rounded-xl border border-ui-red-line bg-ui-red-bg p-4 text-ui-red-fg">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-none" />
                <p className="text-sm">The reconciliation ran, but its FlightAware log entry could not be saved. Check the backend database configuration before relying on the log view.</p>
              </div>
            )}

            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard label="Flight components" value={result.flight_components_scanned} detail={`${result.trips_scanned || 0} trips scanned`} />
              <MetricCard label="Flight documents to create" value={result.flight_documents_created} tone="purple" />
              <MetricCard label="Stored records to update" value={(result.flight_documents_updated || 0) + (result.trip_components_updated || 0)} tone="amber" />
              <MetricCard label="Missing alert subscribers" value={result.missing_alert_subscribers} tone="amber" />
              <MetricCard label="Provider alerts checked" value={result.provider_alerts_checked} tone="blue" />
              <MetricCard label="Provider alerts confirmed" value={result.provider_alerts_confirmed} tone="green" />
              <MetricCard label="Provider alerts missing" value={result.provider_alerts_missing} tone="amber" detail="Not present under the current API key" />
              <MetricCard label="Registrations planned" value={result.alert_registrations_planned} tone="purple" />
              <MetricCard label="Live refreshes planned" value={result.flight_refreshes_planned} tone="blue" />
              <MetricCard label="Live refreshes succeeded" value={result.flight_refreshes_succeeded} tone="green" />
              <MetricCard label="Schedule lookups planned" value={result.flight_resolutions_planned} tone="blue" />
              <MetricCard label="Schedule lookups succeeded" value={result.flight_resolutions_succeeded} tone="green" />
              <MetricCard label="Lookup failures" value={result.flight_lookup_failures} tone="amber" />
              <MetricCard label="Lookups with no new data" value={result.flight_lookups_without_new_data} tone="amber" />
              <MetricCard label="Flight fields updated" value={result.flight_fields_updated} tone="green" />
              <MetricCard label="Recently checked" value={result.recently_checked_legs} detail="15-minute retry window" tone="blue" />
              <MetricCard label="Operational details complete" value={result.complete_operational_legs} tone="green" />
              <MetricCard label="Alert registrations attempted" value={result.alert_registrations_attempted} tone="green" />
              <MetricCard label="Alert registrations succeeded" value={result.alert_registrations_succeeded} tone="green" />
              <MetricCard label="Callbacks verified" value={result.alert_registrations_verified} tone="green" />
              <MetricCard label="Callbacks unverified" value={result.alert_registrations_unverified} tone="amber" />
              <MetricCard label="Alert registrations failed" value={result.alert_registrations_failed} tone="amber" />
              <MetricCard label="Legacy flights migrated" value={result.legacy_flight_components_migrated} tone="purple" />
              <MetricCard label="Missing lookup identity" value={result.skipped_missing_flight_identity} tone="amber" />
              <MetricCard label="Past flights ignored" value={result.skipped_past_flights} detail="Departed more than 24 hours ago" tone="green" />
              <MetricCard label="Flights without legs" value={result.skipped_without_legs} tone="amber" />
            </div>
          </section>

          {(result.aerodatabox_subscriptions || result.aerodatabox_comparisons_recorded > 0) && (
            <section className={`rounded-2xl border p-5 shadow-ui-card ${cardClass}`} data-testid="aerodatabox-pilot">
              <h2 className="text-lg font-semibold">AeroDataBox shadow pilot</h2>
              <p className={`mt-1 text-sm ${mutedText}`}>
                Each FlightAware refresh above was mirrored to AeroDataBox and compared field by field. Legs were not changed by the comparison.
              </p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <MetricCard label="Comparisons recorded" value={result.aerodatabox_comparisons_recorded} tone="blue" />
                <MetricCard label="AeroDataBox returned data" value={result.aerodatabox_comparisons_with_data} tone="green" />
                <MetricCard label="AeroDataBox units used" value={result.aerodatabox_units_used} tone="purple" detail="2 units per flight status lookup" />
                <MetricCard label="Fields filled from AeroDataBox" value={result.aerodatabox_fields_filled} tone="green" detail={`${result.aerodatabox_legs_supplemented || 0} legs; only fields FlightAware left empty`} />
                {result.aerodatabox_subscriptions && (
                  <>
                    <MetricCard label="Tracked flight numbers" value={result.aerodatabox_subscriptions.desired_flight_numbers} tone="blue" detail="FlightAware alerts within 72 hours" />
                    <MetricCard label="Alert subscriptions active" value={result.aerodatabox_subscriptions.subscriptions_active} tone="green" />
                    <MetricCard label={resultMode === 'apply' ? 'Subscriptions created' : 'Subscriptions planned'} value={resultMode === 'apply' ? result.aerodatabox_subscriptions.subscriptions_created : result.aerodatabox_subscriptions.subscriptions_planned} tone="purple" />
                    <MetricCard label={resultMode === 'apply' ? 'Subscriptions deleted' : 'Subscriptions to delete'} value={resultMode === 'apply' ? result.aerodatabox_subscriptions.subscriptions_deleted : result.aerodatabox_subscriptions.subscriptions_expired_planned} tone="amber" detail="Every tracked leg has flown" />
                    <MetricCard label="Skipped: no live coverage" value={result.aerodatabox_subscriptions.subscriptions_skipped_no_live_coverage} tone="amber" />
                    <MetricCard label="Subscription failures" value={(result.aerodatabox_subscriptions.subscriptions_failed || 0) + (result.aerodatabox_subscriptions.subscriptions_delete_failed || 0)} tone="amber" />
                    {result.aerodatabox_subscriptions.balance && (
                      <MetricCard label="Alert credit balance" value={result.aerodatabox_subscriptions.balance.balance ?? result.aerodatabox_subscriptions.balance.credits ?? '—'} tone="green" />
                    )}
                    {result.aerodatabox_subscriptions.error && (
                      <MetricCard label="Subscription sync error" value="!" tone="amber" detail={result.aerodatabox_subscriptions.error} />
                    )}
                  </>
                )}
              </div>
            </section>
          )}

          <section className={`rounded-2xl border p-5 shadow-ui-card ${cardClass}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">{resultMode === 'apply' ? 'Reconciliation outcomes' : 'Planned actions'}</h2>
                <p className={`mt-1 text-sm ${mutedText}`}>{result.results?.length || 0} flight components shown.</p>
              </div>
              {resultMode === 'preview' && (
                <button
                  type="button"
                  onClick={() => runReconciliation(false)}
                  disabled={!previewIsCurrent || !backendIsCurrent || Boolean(loadingMode)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-ui-amber-strong px-5 py-2.5 text-sm font-semibold text-ui-on-primary transition hover:bg-ui-amber-strong/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loadingMode === 'apply' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
                  Apply reconciliation
                </button>
              )}
            </div>

            {!result.results?.length ? (
              <div className="mt-5 rounded-xl border border-dashed border-ui-dash p-8 text-center">
                <CheckCircle2 className="mx-auto h-9 w-9 text-ui-green-solid" />
                <div className="mt-2 font-semibold">No repairs required</div>
                <p className={`mt-1 text-sm ${mutedText}`}>The organization’s filed flight records and alert subscribers are already reconciled.</p>
              </div>
            ) : (
              <div className="mt-5 overflow-hidden rounded-xl border border-ui-line">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-ui-line text-sm">
                    <thead className="bg-ui-hover text-ui-ink-2">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">Trip</th>
                        <th className="px-4 py-3 text-left font-semibold">Flight record</th>
                        <th className="px-4 py-3 text-left font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ui-divider">
                      {result.results.map((item, index) => (
                        <tr key={`${item.trip_component_id || item.flight_id}-${index}`}>
                          <td className="px-4 py-3">
                            <div className="font-medium">{item.trip_title || 'Untitled trip'}</div>
                            <div className={`mt-0.5 font-mono text-xs ${mutedText}`}>{item.trip_id}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-mono text-xs">{item.flight_id}</div>
                            {item.flight_labels?.filter(Boolean).length > 0 && (
                              <div className={`mt-1 text-xs ${mutedText}`}>{item.flight_labels.filter(Boolean).join(', ')}</div>
                            )}
                            {item.stored_component_type && (
                              <div className={`mt-0.5 text-xs ${mutedText}`}>Stored type: {item.stored_component_type} · {item.leg_count || 0} leg(s)</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1.5">
                              {(item.actions || []).map((action) => (
                                <span key={action} className="rounded-full bg-ui-hover px-2.5 py-1 text-xs font-medium text-ui-ink-2">
                                  {ACTION_LABELS[action] || action}
                                </span>
                              ))}
                            </div>
                            {item.lookup_outcomes?.length > 0 && (
                              <div className="mt-3 space-y-2">
                                {item.lookup_outcomes.map((outcome, outcomeIndex) => {
                                  const outcomeTone = outcome.status === 'updated'
                                    ? 'border-ui-green-line bg-ui-green-bg text-ui-green-fg'
                                    : outcome.status === 'no_new_data'
                                      ? 'border-ui-blue-line bg-ui-blue-bg text-ui-blue-fg'
                                      : 'border-ui-red-line bg-ui-red-bg text-ui-red-fg';
                                  return (
                                    <div key={`${outcome.leg_number}-${outcome.operation}-${outcomeIndex}`} className={`rounded-lg border p-2.5 text-xs ${outcomeTone}`}>
                                      <div className="font-semibold">Leg {outcome.leg_number} · {outcome.operation === 'live_refresh' ? 'Live refresh' : 'Schedule lookup'} · {outcome.status.replaceAll('_', ' ')}</div>
                                      <div className="mt-1">{outcome.message || 'No response detail was provided.'}</div>
                                      {outcome.updated_fields?.length > 0 && (
                                        <div className="mt-1 font-medium">Updated: {outcome.updated_fields.join(', ')}</div>
                                      )}
                                      {!outcome.updated_fields?.length && outcome.missing_fields_before?.length > 0 && (
                                        <div className="mt-1 opacity-80">Still missing: {outcome.missing_fields_before.join(', ')}</div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {item.registration_outcomes?.length > 0 && (
                              <div className="mt-3 space-y-2">
                                {item.registration_outcomes.map((outcome, outcomeIndex) => {
                                  const successful = ['confirmed', 'registered', 'registered_and_verified', 'handled_with_shared_alert'].includes(outcome.status);
                                  const planned = outcome.status === 'planned';
                                  const outcomeTone = successful
                                    ? 'border-ui-green-line bg-ui-green-bg text-ui-green-fg'
                                    : planned
                                      ? 'border-ui-blue-line bg-ui-blue-bg text-ui-blue-fg'
                                      : 'border-ui-red-line bg-ui-red-bg text-ui-red-fg';
                                  return (
                                    <div key={`${outcome.alert_doc_id || outcome.leg_number}-${outcomeIndex}`} className={`rounded-lg border p-2.5 text-xs ${outcomeTone}`}>
                                      <div className="font-semibold">
                                        {outcome.leg_number ? `Leg ${outcome.leg_number}` : 'Stored alert'} · Registration · {outcome.status.replaceAll('_', ' ')}
                                      </div>
                                      {outcome.reasons?.length > 0 && (
                                        <div className="mt-1">Reason: {outcome.reasons.join(', ').replaceAll('_', ' ')}</div>
                                      )}
                                      {outcome.message && <div className="mt-1">{outcome.message}</div>}
                                      {outcome.callback_verification && (
                                        <div className="mt-2 space-y-1 border-t border-current/20 pt-2">
                                          <div>Provider callback: <span className="font-mono">{outcome.callback_verification.provider_callback_base || 'Not supplied'}</span></div>
                                          <div>Configured callback: <span className="font-mono">{outcome.callback_verification.configured_callback_base || 'Not configured'}</span></div>
                                          <div>
                                            Base URL: {outcome.callback_verification.callback_base_matches ? 'matched' : 'mismatch'} · Query: {outcome.callback_verification.callback_query_matches ? 'matched' : 'mismatch'} · Alert ID: {outcome.callback_verification.alert_id_matches ? 'matched' : 'mismatch'} · Secret: {outcome.callback_verification.secret_matches ? 'matched' : 'mismatch'}
                                          </div>
                                          <div>Provider alert enabled: {outcome.provider_enabled === true ? 'yes' : outcome.provider_enabled === false ? 'no' : 'not confirmed'}</div>
                                        </div>
                                      )}
                                      {outcome.new_fa_alert_id && (
                                        <div className="mt-1 font-mono opacity-80">FlightAware alert: {outcome.new_fa_alert_id}</div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {resultMode === 'apply' && (
              <button
                type="button"
                onClick={() => runReconciliation(true)}
                disabled={Boolean(loadingMode)}
                className="mt-5 inline-flex items-center gap-2 rounded-lg border border-ui-control px-4 py-2 text-sm font-semibold text-ui-ink hover:bg-ui-hover"
              >
                <RefreshCw className="h-4 w-4" /> Verify with another dry run
              </button>
            )}
          </section>
        </>
      )}

      <FlightDataPrecedencePanel />
    </div>
  );
};

export default FlightAwareReconciliation;
